import { ChangeDetectionStrategy, Component, computed, input, model, signal } from "@angular/core";
import { StorePage } from "@front/m19-api-client";
import { IFormFieldComponent, ILabelComponent, ISelectMenuImports } from "@front/m19-ui";

interface StorePageGroup {
  storeId: string;
  storeName: string;
  pages: StorePage[];
}

@Component({
  selector: "app-store-select-menu",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IFormFieldComponent, ILabelComponent, ISelectMenuImports],
  host: {
    class: "block",
  },
  template: `
    <IFormField>
      <label iLabel>
        {{ label() }}
        <span class="text-destructive">*</span>
      </label>
      <ISelectMenu fitTriggerWidth by="storePageId" [(value)]="selectedStorePage" [(searchQuery)]="searchQuery">
        <ISelectTrigger class="flex w-full">
          <ISelectValue [placeholder]="label()">
            <div *iSelectValue="let selected">
              <div class="flex items-center gap-1">
                <span [class.text-muted-foreground]="mode() === 'page'">{{ selected.storeName }}</span>
                @if (mode() === "page") {
                  <span class="text-muted-foreground">/</span>
                  <span class="text-foreground">{{ selected.storePageName }}</span>
                }
              </div>
            </div>
          </ISelectValue>
        </ISelectTrigger>
        <ISelectContent>
          @for (group of filteredGroupedStores(); track group.storeId) {
            @if (mode() === "page") {
              <div class="type-label text-muted-foreground px-2 py-0.5">{{ group.storeName }}</div>
            }
            @for (page of group.pages; track page.storePageId) {
              @if (mode() === "page") {
                <ISelectOption [value]="page">{{ page.storePageName }}</ISelectOption>
              } @else {
                <ISelectOption [value]="page">{{ page.storeName }}</ISelectOption>
              }
            }
          }
        </ISelectContent>
      </ISelectMenu>
    </IFormField>
  `,
})
export class StoreSelectMenuComponent {
  readonly label = input.required<string>();
  readonly mode = input<"store" | "page">("page");
  readonly selectedStorePage = model<StorePage | undefined | null>(undefined);

  readonly searchQuery = signal("");

  readonly stores = input.required<StorePage[]>();
  readonly storeSpotlightAvailableStores = input.required<StorePage[]>();

  private readonly groupedStores = computed((): StorePageGroup[] => {
    const groups = new Map<string, StorePageGroup>();
    const stores = this.mode() === "store" ? this.storeSpotlightAvailableStores() : this.stores();
    for (const page of stores) {
      const existing = groups.get(page.storeId);
      if (existing) {
        existing.pages.push(page);
      } else {
        groups.set(page.storeId, {
          storeId: page.storeId,
          storeName: page.storeName ?? page.storeId,
          pages: [page],
        });
      }
    }
    return [...groups.values()];
  });

  protected readonly filteredGroupedStores = computed((): StorePageGroup[] => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.groupedStores();
    }

    const filtered: StorePageGroup[] = [];
    for (const group of this.groupedStores()) {
      if (group.storeName.toLowerCase().includes(query)) {
        filtered.push(group);
      } else {
        const pages = group.pages.filter((page) => page.storePageName.toLowerCase().includes(query));
        if (pages.length > 0) {
          filtered.push({ ...group, pages });
        }
      }
    }
    return filtered;
  });
}
